import React, { useState, useCallback } from 'react';
import { lookupCreditCheck, CreditCheckLookupData, CreditAgreement } from '../api/creditCheckLookup';
import './Tasks.css';

const BUREAUS = [
  { key: 'equifax', name: 'Equifax' },
  { key: 'transunion', name: 'TransUnion' },
] as const;

/** Plain-English problems with the lookup: a bureau we couldn't reach, or checks we couldn't load. */
const bureauProblems = (data: CreditCheckLookupData): string[] => {
  const problems: string[] = [];
  BUREAUS.forEach(({ key, name }) => {
    const status = data.bureaus?.[key];
    if (!status) return;
    if (!status.reachable) problems.push(`Couldn't reach ${name} — try again.`);
    if (status.failedReports > 0) {
      const checks = status.failedReports === 1 ? 'check' : 'checks';
      problems.push(`Couldn't load ${status.failedReports} ${name} ${checks} — try again.`);
    }
  });
  return problems;
};

const isReachable = (data: CreditCheckLookupData, key: 'equifax' | 'transunion') =>
  data.bureaus?.[key]?.reachable !== false;

/** A bureau we can honestly say had nothing: reached, and no check left unloaded. */
const searchedFully = (data: CreditCheckLookupData, key: 'equifax' | 'transunion') =>
  isReachable(data, key) && !(data.bureaus?.[key]?.failedReports);

const formatDate = (value: string | null | undefined): string => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return value; }
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

const CreditCheckLookup: React.FC = () => {
  const [referenceInput, setReferenceInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreditCheckLookupData | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState<CreditAgreement | null>(null);

  const handleLookup = useCallback(async () => {
    const ref = referenceInput.trim();
    if (!ref) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setNotFound(false);
    setSelectedAgreement(null);
    try {
      const response = await lookupCreditCheck(ref);
      if (!response.success) { setError(response.error || 'Lookup failed'); return; }
      // Kept even when nothing was found: it says which bureaus could be searched.
      setResult(response.data);
      if (!response.found) setNotFound(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [referenceInput]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleLookup();
  };

  return (
    <div className="tasks-container">
      <div className="tasks-header">
        <h1>Credit Check Lookup</h1>
      </div>

      {/* Search */}
      <div style={{ background: 'white', padding: '20px', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 20 }}>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#333' }}>Enter FC or FM Reference</label>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <input
            type="text"
            value={referenceInput}
            onChange={(e) => setReferenceInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g. FC197984.001"
            style={{ flex: 1, maxWidth: 320, padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 }}
            disabled={loading}
            autoFocus
          />
          <button
            onClick={handleLookup}
            disabled={loading || !referenceInput.trim()}
            style={{
              padding: '8px 20px', backgroundColor: '#4472C4', color: 'white', border: 'none',
              borderRadius: 6, fontWeight: 600, cursor: loading || !referenceInput.trim() ? 'not-allowed' : 'pointer',
              opacity: loading || !referenceInput.trim() ? 0.6 : 1,
            }}
          >
            {loading ? 'Searching...' : 'Look Up'}
          </button>
        </div>
        <p style={{ margin: '8px 0 0', color: '#666', fontSize: 12 }}>Searches Valifi historical records — does not run a new credit check.</p>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: '#fff3f3', border: '1px solid #e74c3c', borderRadius: 8, padding: '16px 20px', marginBottom: 20, color: '#c0392b' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Bureau problems: never shown as "0 checks" */}
      {result && bureauProblems(result).length > 0 && (
        <div role="alert" style={{ background: '#fff3f3', border: '1px solid #e74c3c', borderRadius: 8, padding: '16px 20px', marginBottom: 20, color: '#c0392b' }}>
          {bureauProblems(result).map((problem) => (
            <div key={problem} style={{ fontWeight: 600 }}>{problem}</div>
          ))}
        </div>
      )}

      {/* Not found */}
      {notFound && result && (() => {
        const searched = BUREAUS.filter(({ key }) => searchedFully(result, key)).map(({ name }) => name);
        if (searched.length === 0) return null;
        return (
          <div style={{ background: '#fffbe6', border: '1px solid #f0ad4e', borderRadius: 8, padding: '16px 20px', marginBottom: 20, color: '#7d6608' }}>
            {searched.length === BUREAUS.length ? (
              <>No credit checks found for reference <strong>{referenceInput}</strong> in the last 18 months.</>
            ) : (
              <>Nothing found on {searched.join(' or ')} for reference <strong>{referenceInput}</strong> in the last 18 months.</>
            )}
          </div>
        );
      })()}

      {/* Results */}
      {result && !notFound && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 20 }}>
            {[
              { label: 'Reference', value: result.reference },
              { label: 'Client Name', value: result.clientName || '—' },
              { label: 'Search Date', value: formatDate(result.searchDate) },
              { label: 'Equifax Checks', value: String(result.equifaxChecks), down: !isReachable(result, 'equifax') },
              { label: 'TransUnion Checks', value: String(result.transunionChecks), down: !isReachable(result, 'transunion') },
              { label: 'Total Agreements', value: String(result.totalAgreements) },
            ].map(({ label, value, down }) => (
              <div key={label} style={{ background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                {down ? (
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#c0392b' }}>Couldn't reach</div>
                ) : (
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#333' }}>{value}</div>
                )}
              </div>
            ))}
          </div>

          {/* Checks run under the client's other claim numbers */}
          {result.siblingRefs && result.siblingRefs.length > 0 && (
            <div style={{ background: '#eef4fc', border: '1px solid #b6cff0', borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: '#1f4e8c' }}>
              Also includes checks run under {result.siblingRefs.join(', ')} — the same client's other {result.siblingRefs.length === 1 ? 'claim' : 'claims'}.
            </div>
          )}

          {/* Agreements table */}
          {result.agreements.length === 0 ? (
            <div style={{ background: 'white', padding: 20, borderRadius: 8, color: '#666' }}>
              Credit checks were found but no agreements were extracted from the reports.
            </div>
          ) : (
            <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.08)', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#4472C4', color: 'white' }}>
                    {['Bureau', 'Checked Under', 'Lender', 'Account Number', 'Type', 'Start Date', 'End Date', 'Balance', 'Monthly', 'Status', 'Existing Claim'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.agreements.map((agr, idx) => (
                    <tr
                      key={`${agr.bureau}-${agr.accountNumber}-${idx}`}
                      style={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa', cursor: 'pointer', borderBottom: '1px solid #e9ecef' }}
                      onClick={() => setSelectedAgreement(agr)}
                    >
                      <td style={{ padding: '9px 12px' }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                          color: 'white', backgroundColor: agr.bureau === 'equifax' ? '#c0392b' : '#2980b9', textTransform: 'uppercase'
                        }}>
                          {agr.bureau === 'equifax' ? 'Equifax' : 'TransUnion'}
                        </span>
                      </td>
                      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                        {agr.checkedUnderSibling ? (
                          <span
                            title="Checked under the same client's other claim"
                            style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#eef4fc', color: '#1f4e8c', border: '1px solid #b6cff0' }}
                          >
                            {agr.checkedUnder}
                          </span>
                        ) : (
                          agr.checkedUnder || '—'
                        )}
                      </td>
                      <td style={{ padding: '9px 12px', fontWeight: 600 }}>{agr.lenderName}</td>
                      <td style={{ padding: '9px 12px', fontFamily: 'monospace' }}>{agr.accountNumber}</td>
                      <td style={{ padding: '9px 12px' }}>{agr.accountType}</td>
                      <td style={{ padding: '9px 12px' }}>{formatDate(agr.startDate)}</td>
                      <td style={{ padding: '9px 12px' }}>{formatDate(agr.endDate)}</td>
                      <td style={{ padding: '9px 12px' }}>{formatCurrency(agr.currentBalance)}</td>
                      <td style={{ padding: '9px 12px' }}>{formatCurrency(agr.monthlyPayment)}</td>
                      <td style={{ padding: '9px 12px' }}>{agr.currentStatus}</td>
                      <td style={{ padding: '9px 12px' }}>
                        {agr.existingClaim ? (
                          <span style={{ display: 'inline-block', padding: '2px 8px', background: '#e8f5e9', color: '#2e7d32', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                            {agr.existingClaim.claimId} ({agr.existingClaim.status})
                          </span>
                        ) : (
                          <span style={{ color: '#999' }}>None</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      {selectedAgreement && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setSelectedAgreement(null)}
        >
          <div
            style={{ background: 'white', borderRadius: 10, padding: 28, maxWidth: 520, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Agreement Detail</h2>
              <button onClick={() => setSelectedAgreement(null)} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#666' }}>x</button>
            </div>
            {[
              ['Bureau', selectedAgreement.bureau === 'equifax' ? 'Equifax' : 'TransUnion'],
              ['Checked Under', selectedAgreement.checkedUnderSibling
                ? `${selectedAgreement.checkedUnder} (same client, other claim)`
                : (selectedAgreement.checkedUnder || '—')],
              ['Lender', selectedAgreement.lenderName],
              ['Account Number', selectedAgreement.accountNumber],
              ['Type', selectedAgreement.accountType],
              ['Start Date', formatDate(selectedAgreement.startDate)],
              ['End Date', formatDate(selectedAgreement.endDate)],
              ['Current Balance', formatCurrency(selectedAgreement.currentBalance)],
              ['Monthly Payment', formatCurrency(selectedAgreement.monthlyPayment)],
              ['Status', selectedAgreement.currentStatus],
              ['Repayment Term', selectedAgreement.repaymentTerm || '—'],
              ['Payment Frequency', selectedAgreement.paymentFrequency || '—'],
              ['Source', selectedAgreement.sourcedFrom || '—'],
              ['Report ID', selectedAgreement.reportId ? String(selectedAgreement.reportId) : '—'],
              ['Search Date', formatDate(selectedAgreement.searchDate)],
              ['Existing Claim', selectedAgreement.existingClaim ? `${selectedAgreement.existingClaim.claimId} (${selectedAgreement.existingClaim.status})` : 'None'],
            ].map(([label, value]) => (
              <div key={String(label)} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ color: '#666', fontWeight: 500 }}>{label}</span>
                <span style={{ fontWeight: 600, maxWidth: '55%', textAlign: 'right' }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditCheckLookup;
